/**
 * TASK-COMBAT-VIZ-010: Combat Grid Renderer Implementation
 *
 * Extends DeploymentGridRenderer with animated sprite support for live combat.
 * Handles smooth interpolation, animation state transitions, and 60 FPS rendering.
 */

import * as PIXI from 'pixi.js';
import { HexGridRenderer } from '../DeploymentGrid/DeploymentGridRenderer';
import type { AxialCoordinate, HexFootprint, HexTileAssignment } from '@drawn-of-war/shared';
import { getFootprintCenter, getFootprintHexes } from '@drawn-of-war/shared';
import type { AnimationData } from '../../services/animation-asset-mapper';
import { AnimationState } from '../../services/unit-animation-state-machine';
import {
  BASE_TILES, OVERLAY_TILES, getAllTilePaths,
  type BaseTileId, type OverlayTileId
} from '../BattlefieldEditor/tile-registry';

/**
 * How much of the hex a creature sprite fills (1.0 = exactly one hex diameter).
 * Values > 1.0 let sprites overflow their hex for a chunkier, more readable look.
 * Logical occupancy (1 creature per hex) is unaffected — this is purely visual.
 */
const CREATURE_HEX_FILL_RATIO = 1.3;

/**
 * Archetype-based size multipliers (fallback for creatures without stored size)
 * Used only for legacy creatures that don't have Claude-assigned size
 */
const ARCHETYPE_SIZE_MULTIPLIERS: Record<string, number> = {
  tank: 1.25,
  melee_dps: 1.05,
  ranged_dps: 0.95,
  mage: 0.85,
  support: 0.90,
};

/**
 * Default size multiplier for unknown archetypes
 */
const DEFAULT_SIZE_MULTIPLIER = 1.0;

/**
 * Hash-based variation range (±10%)
 * Adds subtle individual variation to creatures of the same base size
 */
const SIZE_VARIATION_RANGE = 0.1;

/**
 * Calculate a deterministic size multiplier for a creature
 *
 * For new creatures: Uses Claude-assigned size (0.3-3.0) + ±10% hash variation
 * For legacy creatures: Falls back to archetype-based sizing + ±10% hash variation
 *
 * @param unitId - Unique unit identifier (used for deterministic hash)
 * @param archetype - Creature archetype (tank, melee_dps, etc.) - fallback only
 * @param storedSize - Claude-assigned size from creature document (0.3-3.0)
 * @returns Size multiplier to apply to sprite scale
 */
export function calculateCreatureSizeMultiplier(
  unitId: string,
  archetype?: string,
  storedSize?: number
): number {
  // Determine base multiplier:
  // 1. Use stored size if available (Claude's creative assignment)
  // 2. Fall back to archetype-based sizing for legacy creatures
  let baseMultiplier: number;

  // Cap at 2.0 — sprites can overflow their hex visually; logical occupancy unchanged
  if (storedSize !== undefined && storedSize >= 0.3 && storedSize <= 2.0) {
    // New system: Claude assigned size based on visual appearance
    baseMultiplier = storedSize;
  } else {
    // Legacy fallback: archetype-based sizing
    baseMultiplier = archetype
      ? (ARCHETYPE_SIZE_MULTIPLIERS[archetype] ?? DEFAULT_SIZE_MULTIPLIER)
      : DEFAULT_SIZE_MULTIPLIER;
  }

  // Calculate deterministic hash-based variation from unitId
  // Uses simple string hashing to get consistent value between 0-1
  let hash = 0;
  for (let i = 0; i < unitId.length; i++) {
    const char = unitId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Normalize hash to range [-1, 1]
  const normalizedHash = (Math.abs(hash) % 1000) / 500 - 1;

  // Apply variation: baseMultiplier ± (SIZE_VARIATION_RANGE * normalizedHash)
  const variation = normalizedHash * SIZE_VARIATION_RANGE;
  const finalMultiplier = baseMultiplier + variation;

  // Hard cap at 2.0 — sprites can overflow hex visually; logical occupancy unchanged
  return Math.min(finalMultiplier, 2.0);
}

/**
 * Animation completion event
 */
export interface AnimationCompleteEvent {
  unitId: string;
  animationState: AnimationState;
}

/**
 * Animated sprite wrapper
 */
/**
 * Projectile data for a creature (cached from generation)
 */
export interface CreatureProjectileData {
  /** Gallery key for lookup */
  key: string;
  /** Animation frames as base64 PNGs */
  frames: string[];
  /** Element type for fallback color */
  element: string;
}

interface AnimatedSpriteData {
  container: PIXI.Container;   // Store container reference
  sprite: PIXI.AnimatedSprite;
  unitId: string;
  animationState: AnimationState;
  isPlaying: boolean;
  loop: boolean;
  textures: PIXI.Texture[];
  scale: { x: number; y: number };
  x: number;
  y: number;
  onComplete?: () => void;

  // Cached data for animation state changes
  battlefieldViews?: any;      // BattlefieldDirectionalViews from creature data
  playerId?: 'player1' | 'player2';
  direction?: number;
  projectile?: CreatureProjectileData; // Projectile sprite data for ranged attacks

  // Sub-hex positioning (L3-VIS-SUBHEX-001)
  subHexOffset: { x: number; y: number };  // Pixel offset from hex center
  lastHex?: { q: number; r: number };       // For hex change detection

  // L3-MULTI-HEX: Footprint for multi-hex unit rendering
  footprint?: HexFootprint;

  // Attack wind-up offset (temporary lean toward/away from target)
  windupOffset?: { x: number; y: number; expiresAt: number };
}

/**
 * CombatGridRenderer
 *
 * Extends HexGridRenderer with animated sprite support for combat visualization.
 */
export class CombatGridRenderer extends HexGridRenderer {
  private animatedSprites: Map<string, AnimatedSpriteData> = new Map();
  private animationCompleteCallbacks: ((event: AnimationCompleteEvent) => void)[] = [];
  private lastUpdateTime: number = 0;
  private pendingCreations: Set<string> = new Set();

  // Tile rendering layers and cache
  private baseTileLayer: PIXI.Container | null = null;
  private overlayTileLayer: PIXI.Container | null = null;
  private tileTextureCache: Map<string, PIXI.Texture> = new Map();

  /**
   * Load and render tile textures from a MapConfig tiles record.
   * Call this after init() and before combat starts.
   */
  public override async loadTiles(tiles: Record<string, HexTileAssignment>): Promise<void> {
    if (!tiles || Object.keys(tiles).length === 0) return;

    // Preload all tile textures
    const paths = getAllTilePaths();
    for (const path of paths) {
      if (!this.tileTextureCache.has(path)) {
        try {
          const texture = await PIXI.Assets.load<PIXI.Texture>(path);
          this.tileTextureCache.set(path, texture);
        } catch (err) {
          console.warn(`[CombatGridRenderer] Failed to load tile: ${path}`, err);
        }
      }
    }

    // Create tile layers
    this.baseTileLayer = new PIXI.Container();
    this.overlayTileLayer = new PIXI.Container();

    // Hide the default cyber-arena terrain when tiles provide the ground treatment
    this.hideTerrainBackground();
    if (this.terrainRenderer) {
      this.terrainRenderer.getEffectsContainer().visible = false;
      this.terrainRenderer.getDustContainer().visible = false;
    }

    // If a custom background image exists, move it to the very back (behind tiles)
    let bgLayerCount = 0;
    if (this.customBgSprite) {
      this.worldContainer.removeChild(this.customBgSprite);
      this.worldContainer.addChildAt(this.customBgSprite, 0);
      bgLayerCount = 1;
    }

    // Load a persistent background behind tiles if configured (legacy path)
    if (!this.customBgSprite && this.tileBackgroundUrl) {
      await this.loadTileBackground();
      bgLayerCount = 1;
    }

    // Insert tile layers after background (if present)
    this.worldContainer.addChildAt(this.baseTileLayer, bgLayerCount);
    this.worldContainer.addChildAt(this.overlayTileLayer, bgLayerCount + 1);

    const offset = this.getGridOffset();

    // Render base tiles (hex-clipped)
    for (const [key, assignment] of Object.entries(tiles)) {
      const baseDef = BASE_TILES[assignment.baseTile as BaseTileId];
      if (!baseDef) continue;

      const texture = this.tileTextureCache.get(baseDef.path);
      if (!texture) continue;

      const [qStr, rStr] = key.split(',');
      const hex = { q: parseInt(qStr!, 10), r: parseInt(rStr!, 10) };
      const pixel = this.hexToPixel(hex);

      const sprite = new PIXI.Sprite(texture);
      const imgSize = this.hexGrid.hexSize * 2.2;
      sprite.width = imgSize;
      sprite.height = imgSize;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = pixel.x;
      sprite.y = pixel.y;

      // Hex polygon mask
      const mask = new PIXI.Graphics();
      mask.poly(this.getHexVertices(this.hexGrid.hexSize));
      mask.fill({ color: 0xffffff });
      mask.x = pixel.x;
      mask.y = pixel.y;

      sprite.mask = mask;
      this.baseTileLayer.addChild(mask);
      this.baseTileLayer.addChild(sprite);
    }

    // Collect and Y-sort overlays
    const overlays: Array<{
      hex: AxialCoordinate;
      overlayId: OverlayTileId;
      pixelY: number;
    }> = [];

    for (const [key, assignment] of Object.entries(tiles)) {
      if (!assignment.overlay) continue;
      const overlayDef = OVERLAY_TILES[assignment.overlay as OverlayTileId];
      if (!overlayDef) continue;

      const [qStr, rStr] = key.split(',');
      const hex = { q: parseInt(qStr!, 10), r: parseInt(rStr!, 10) };
      const pixel = this.hexToPixel(hex);
      overlays.push({ hex, overlayId: assignment.overlay as OverlayTileId, pixelY: pixel.y });
    }

    overlays.sort((a, b) => a.pixelY - b.pixelY);

    for (const { hex, overlayId } of overlays) {
      const overlayDef = OVERLAY_TILES[overlayId];
      const texture = this.tileTextureCache.get(overlayDef.path);
      if (!texture) continue;

      const pixel = this.hexToPixel(hex);
      const sprite = new PIXI.Sprite(texture);
      const size = this.hexGrid.hexSize * 2 * overlayDef.scale;
      sprite.width = size;
      sprite.height = size;
      sprite.anchor.set(0.5, 0.5);
      sprite.x = pixel.x;
      sprite.y = pixel.y + overlayDef.offsetY;

      this.overlayTileLayer.addChild(sprite);
    }

    this.constrainCameraToGrid();

    console.log(`[CombatGridRenderer] Loaded ${Object.keys(tiles).length} tiles, ${overlays.length} overlays`);
  }

  /**
   * Check if animations are supported
   */
  public supportsAnimations(): boolean {
    return true;
  }

  /**
   * Get the PixiJS Application instance
   * Required by CombatVisualizationManager to call app.render()
   * TASK-BUGFIX-RENDER-001
   */
  public getApp(): PIXI.Application {
    return this.app; // app is protected in parent HexGridRenderer
  }

  /**
   * Change animation state for an existing sprite
   * This swaps the texture array to show different animation frames
   */
  public async changeAnimationState(unitId: string, newState: AnimationState, direction?: number): Promise<void> {
    const spriteData = this.animatedSprites.get(unitId);

    if (!spriteData) {
      // Check if sprite creation is pending (async path still loading)
      if (this.pendingCreations.has(unitId)) {
        // Sprite is being created - silently skip, it will be in IDLE state when ready
        return;
      }
      console.warn('[CombatGridRenderer] Cannot change animation state - sprite not found:', unitId);
      return;
    }

    // Update direction if provided (e.g., face target when attacking)
    if (direction !== undefined) {
      spriteData.direction = direction;
    }

    // Skip if already in this state and direction hasn't changed
    if (spriteData.animationState === newState && direction === undefined) {
      return;
    }

    console.log('[CombatGridRenderer] Changing animation state:', {
      unitId,
      from: spriteData.animationState,
      to: newState,
      direction: spriteData.direction
    });

    // Check if we have cached battlefield views
    if (!spriteData.battlefieldViews) {
      console.warn('[CombatGridRenderer] Cannot change animation - no cached battlefield views:', unitId);
      spriteData.animationState = newState; // Update state flag anyway
      return;
    }

    try {
      // Get new animation data for the requested state
      const animationData = this.selectDirectionalAnimation(
        spriteData.battlefieldViews,
        spriteData.direction || 1,
        newState
      );

      // Load new textures (try cache first)
      const textures: PIXI.Texture[] = [];

      for (let index = 0; index < animationData.frames.length; index++) {
        const frame = animationData.frames[index];
        try {
          // FIX: Normalize frame URL to handle raw base64 strings (challenge creatures)
          const normalizedFrame = this.normalizeImageUrl(frame);

          // Try synchronous cache retrieval
          const cached = PIXI.Assets.get(normalizedFrame) as PIXI.Texture;
          if (cached) {
            textures.push(cached);
          } else {
            // FIX: For data URIs, use Assets.add + Assets.load (PIXI.js v8 fix)
            if (normalizedFrame.startsWith('data:')) {
              // Generate unique asset key to avoid caching issues
              const assetKey = `combat-anim-${spriteData.unitId}-${newState}-${index}-${Date.now()}`;

              // Register the data URI asset
              if (!PIXI.Assets.cache.has(assetKey)) {
                PIXI.Assets.add({ alias: assetKey, src: normalizedFrame });
              }

              try {
                const loaded = await PIXI.Assets.load(assetKey);
                textures.push(loaded);
              } catch (assetsError) {
                console.warn(`[CombatGridRenderer] Assets.load failed for data URI, trying Texture.from fallback`);
                // Fallback: use Texture.from (PIXI.js v8)
                const fallbackTexture = PIXI.Texture.from(normalizedFrame);
                // Small delay for PIXI.js v8 to process the texture
                await new Promise(resolve => setTimeout(resolve, 10));
                textures.push(fallbackTexture);
              }
            } else {
              // For HTTP URLs, use Assets.load directly
              const loaded = await PIXI.Assets.load(normalizedFrame);
              textures.push(loaded);
            }
          }
        } catch (error) {
          console.error('[CombatGridRenderer] Failed to load texture:', frame, error);
          // Continue with other frames
        }
      }

      if (textures.length === 0) {
        console.error('[CombatGridRenderer] No textures loaded for animation state:', newState);
        return;
      }

      // Update sprite state
      spriteData.animationState = newState;
      spriteData.loop = animationData.loop;
      spriteData.textures = textures;

      // Swap AnimatedSprite textures
      spriteData.sprite.textures = textures;
      spriteData.sprite.loop = animationData.loop;

      // Apply mirroring for western-facing directions
      const currentScaleX = Math.abs(spriteData.sprite.scale.x);
      spriteData.sprite.scale.x = animationData.shouldMirror ? -currentScaleX : currentScaleX;

      // Apply state-aware frame duration (same formula as sprite creation path)
      spriteData.sprite.animationSpeed = animationData.frameDuration / 1000 * 1.0;

      // Setup completion handler for one-shot animations
      if (!animationData.loop) {
        spriteData.sprite.onComplete = () => {
          console.log('[CombatGridRenderer] Animation completed:', unitId, newState);
          this.triggerAnimationComplete({
            unitId,
            animationState: newState
          });
        };
      } else {
        // Clear completion handler for looping animations
        spriteData.sprite.onComplete = null;
      }

      // Restart playback from frame 0
      spriteData.sprite.gotoAndPlay(0);

      console.log('[CombatGridRenderer] Successfully changed animation state:', {
        unitId,
        newState,
        frameCount: textures.length,
        loop: animationData.loop
      });
    } catch (error) {
      console.error('[CombatGridRenderer] Error changing animation state:', error);
    }
  }

  /**
   * Render creature with unit ID for proper sprite lifecycle management
   * This is the combat-specific method that prevents ghost trails
   *
   * @param sizeMultiplier - Optional size multiplier for creature variety (default: 1.0)
   *                         Use calculateCreatureSizeMultiplier() to derive from archetype
   */
  public renderCreatureWithId(
    unitId: string,
    hex: AxialCoordinate,
    _creatureName: string,
    playerId: 'player1' | 'player2',
    spriteData?: any,
    opacity: number = 1.0,
    direction: number = 1,
    sizeMultiplier: number = 1.0
  ): void {
    // Check if unit already has a sprite
    const existing = this.animatedSprites.get(unitId);

    if (existing) {
      // UPDATE EXISTING SPRITE IN-PLACE
      this.updateSpritePosition(existing, hex);
      // Animation state changes handled by renderAnimatedCreature if needed
      return;
    }

    // NEW UNIT - Extract animation data from spriteData
    const battlefieldViews = this.extractBattlefieldViews(spriteData);
    // Extract projectile data for ranged attacks
    const projectileData = this.extractProjectileData(spriteData);
    // L3-MULTI-HEX: Extract footprint for multi-hex units
    const footprint = this.extractFootprint(spriteData);

    if (battlefieldViews) {
      const animationData = this.selectDirectionalAnimation(battlefieldViews, direction);

      // FIX: Check if frames are valid (non-empty strings) before attempting texture load
      // Placeholder creatures have empty frame arrays that fall back to empty sprite strings
      const validFrames = animationData.frames.filter(f => f && f.length > 0);
      if (validFrames.length === 0) {
        console.warn('[CombatGridRenderer] No valid sprite frames for unit:', unitId, '- rendering placeholder');
        this.renderPlaceholderSprite(unitId, hex, playerId, _creatureName, sizeMultiplier, footprint);
        return;
      }

      // Try synchronous texture retrieval from cache
      const textures: (PIXI.Texture | null)[] = animationData.frames.map(frame => {
        try {
          const texture = PIXI.Assets.get(frame);
          // Assets.get() returns undefined if not in cache, not an error
          return texture !== undefined ? texture as PIXI.Texture : null;
        } catch {
          return null;
        }
      });

      // Check if all textures are cached
      const allCached = textures.every(t => t !== null);

      if (allCached) {
        // CACHE HIT - Create sprite synchronously
        console.log('[CombatGridRenderer] Cache HIT - creating sprite synchronously:', unitId, 'sizeMultiplier:', sizeMultiplier);

        // Cache battlefield views and projectile data for createSpriteSync to access
        (this as any).lastBattlefieldViews = battlefieldViews;
        (this as any).lastDirection = direction;
        (this as any).lastSizeMultiplier = sizeMultiplier;
        (this as any).lastProjectileData = projectileData;
        (this as any).lastFootprint = footprint;  // L3-MULTI-HEX

        this.createSpriteSync(
          unitId,
          hex,
          playerId,
          AnimationState.IDLE,
          animationData,
          textures as PIXI.Texture[]
        );

        // Clear cache
        delete (this as any).lastBattlefieldViews;
        delete (this as any).lastDirection;
        delete (this as any).lastSizeMultiplier;
        delete (this as any).lastProjectileData;
        delete (this as any).lastFootprint;  // L3-MULTI-HEX
      } else {
        // CACHE MISS - Fall back to async with pending tracker
        console.log('[CombatGridRenderer] Cache MISS - falling back to async load:', unitId, 'sizeMultiplier:', sizeMultiplier);
        // PVE-FIX-003: Pass data directly as parameters to avoid race condition with concurrent async operations
        this.renderAnimatedCreature(
          hex,
          unitId,
          playerId,
          AnimationState.IDLE,
          animationData,
          sizeMultiplier,
          battlefieldViews,  // Pass directly
          direction,         // Pass directly
          projectileData,    // Pass directly
          footprint          // L3-MULTI-HEX: Pass directly
        ).catch(err => {
          console.error('[CombatGridRenderer] Failed to render animated creature:', err);
        });
      }
    } else {
      // Fallback for non-directional sprites (using unitId tracking to prevent ghost trails)
      console.log('[CombatGridRenderer] No battlefieldDirectionalViews - using static sprite fallback with unitId tracking');

      // Extract sprite URL from spriteData
      let spriteUrl: string | undefined;
      if (typeof spriteData === 'string') {
        spriteUrl = spriteData;
      } else if (spriteData && typeof spriteData === 'object') {
        spriteUrl = (spriteData as any).sprite || (spriteData as any).battlefieldSprite || (spriteData as any).spriteImageBase64;
      }

      if (!spriteUrl) {
        console.warn('[CombatGridRenderer] No sprite URL found for unit:', unitId);
        return;
      }

      // Convert base64 data to proper data URI if needed
      const processedUrl = this.normalizeImageUrl(spriteUrl);
      console.log('[CombatGridRenderer] Sprite URL type:', {
        original: spriteUrl.substring(0, 50),
        processed: processedUrl.substring(0, 50),
        isDataUri: processedUrl.startsWith('data:')
      });

      // Create static sprite with unitId tracking (prevents ghost trails)
      this.renderStaticSpriteWithId(unitId, hex, playerId, processedUrl, opacity, sizeMultiplier);
    }
  }

  /**
   * Render a placeholder circle for units with missing/invalid sprite data
   * FIX: Ensures visibility for placeholder creatures that lack proper sprites
   */
  private renderPlaceholderSprite(
    unitId: string,
    hex: AxialCoordinate,
    playerId: 'player1' | 'player2',
    creatureName: string,
    sizeMultiplier: number = 1.0,
    footprint?: HexFootprint
  ): void {
    // Check if already exists
    if (this.animatedSprites.has(unitId)) {
      return;
    }

    // Calculate position (L3-MULTI-HEX: use footprint center if available)
    const footprintSize = footprint?.size ?? 1;
    let position: { x: number; y: number };
    if (footprint && footprintSize > 1) {
      const center = getFootprintCenter(hex, footprint);
      position = this.hexToPixel(center);
    } else {
      position = this.hexToPixel(hex);
    }

    // Create container for placeholder
    const container = new PIXI.Container();
    container.position.set(position.x, position.y);

    // Team-colored background circle
    const bgColor = playerId === 'player1' ? 0x3498db : 0xe74c3c;
    const placeholderFill = footprintSize > 1 ? 1.0 : CREATURE_HEX_FILL_RATIO;
    const radius = this.getHexGrid().hexSize * placeholderFill * 0.5 * sizeMultiplier;

    const bg = new PIXI.Graphics();
    bg.circle(0, 0, radius);
    bg.fill({ color: bgColor, alpha: 0.8 });
    container.addChild(bg);

    // Add first letter of creature name
    const text = new PIXI.Text({
      text: creatureName ? creatureName.charAt(0).toUpperCase() : '?',
      style: {
        fontSize: 24 * sizeMultiplier,
        fill: 0xffffff,
        fontWeight: 'bold',
        align: 'center'
      }
    });
    text.anchor.set(0.5);
    container.addChild(text);

    // Add ground indicator (team-colored ellipse)
    const groundIndicator = new PIXI.Graphics();
    const ellipseWidth = this.getHexGrid().hexSize * 0.85 * sizeMultiplier;
    const ellipseHeight = ellipseWidth * 0.3;
    groundIndicator.ellipse(0, radius * 0.6, ellipseWidth, ellipseHeight);
    groundIndicator.fill({ color: bgColor, alpha: 0.4 });
    container.addChildAt(groundIndicator, 0);

    // Add to creature layer
    this.getCreatureLayer().addChild(container);

    // Track in sprites map (so updateSpritePosition works)
    // Provide all required fields for AnimatedSpriteData
    this.animatedSprites.set(unitId, {
      unitId,
      container,
      sprite: container as any, // Use container as sprite reference for placeholder
      animationState: AnimationState.IDLE,
      isPlaying: false,
      loop: false,
      textures: [],
      scale: { x: 1, y: 1 },
      x: position.x,
      y: position.y,
      direction: 1,
      battlefieldViews: undefined,
      projectile: undefined,
      subHexOffset: { x: 0, y: 0 },
      footprint
    });

    console.log('[CombatGridRenderer] Rendered placeholder for unit:', unitId, 'at', hex);
  }

  /**
   * Create animated sprite synchronously using pre-loaded textures
   */
  private createSpriteSync(
    unitId: string,
    hex: AxialCoordinate,
    playerId: 'player1' | 'player2',
    animationState: AnimationState,
    animationData: AnimationData,
    textures: PIXI.Texture[]
  ): void {
    // Validate textures array
    if (!textures || textures.length === 0) {
      console.error('[CombatGridRenderer] createSpriteSync called with empty textures array');
      return;
    }

    // Validate individual textures are not null/destroyed/empty/invalid
    const validTextures = textures.filter((tex, i) => {
      if (!tex) {
        console.error(`[CombatGridRenderer] [${unitId}] Null texture at index ${i} in createSpriteSync`);
        return false;
      }
      if (tex === PIXI.Texture.EMPTY) {
        console.error(`[CombatGridRenderer] [${unitId}] EMPTY texture at index ${i} in createSpriteSync`);
        return false;
      }
      if (!tex.source || tex.source.destroyed) {
        console.error(`[CombatGridRenderer] [${unitId}] Invalid/destroyed texture at index ${i} in createSpriteSync`);
        return false;
      }
      if (!tex.width || !tex.height) {
        console.error(`[CombatGridRenderer] [${unitId}] Zero-dimension texture at index ${i}: ${tex.width}x${tex.height}`);
        return false;
      }
      return true;
    });

    if (validTextures.length === 0) {
      console.error('[CombatGridRenderer] createSpriteSync: All textures invalid for unit:', unitId);
      return;
    }

    // L3-MULTI-HEX: Get footprint from cache
    const cachedFootprint: HexFootprint | undefined = (this as any).lastFootprint;
    const footprintSize = cachedFootprint?.size ?? 1;

    // L3-MULTI-HEX: Calculate position at footprint visual center for multi-hex units
    let position: { x: number; y: number };
    if (cachedFootprint && footprintSize > 1) {
      const center = getFootprintCenter(hex, cachedFootprint);
      position = this.hexToPixel(center);
    } else {
      position = this.hexToPixel(hex);
    }

    // Create animated sprite with validated textures
    const animatedSprite = new PIXI.AnimatedSprite(validTextures);

    // Configure animation
    animatedSprite.animationSpeed = animationData.frameDuration / 1000 * 1.0; // Convert ms to PixiJS speed (increased to 1.0 for proper sync with movement)
    animatedSprite.loop = animationData.loop;
    animatedSprite.anchor.set(0.5);

    // Scale sprite to fill hex — sizeMultiplier contains creature's visual size
    // L3-MULTI-HEX-FIX: Footprint determines occupancy/separation only, NOT visual scale.
    // For multi-hex creatures, sizeMultiplier = creature's stored size (e.g., 1.6, 2.0).
    // Don't multiply by footprintSize — that would make sprites grow when footprint increases.
    const sizeMultiplier = (this as any).lastSizeMultiplier ?? 1.0;
    const fillRatio = footprintSize > 1 ? 1.0 : CREATURE_HEX_FILL_RATIO;
    const targetSize = this.getHexGrid().hexSize * fillRatio * sizeMultiplier;
    const firstTexture = validTextures[0];
    const baseScale = Math.min(
      targetSize / (firstTexture?.width || 100),
      targetSize / (firstTexture?.height || 100)
    );

    // Apply mirroring if needed
    if (animationData.shouldMirror) {
      animatedSprite.scale.set(-baseScale, baseScale);
    } else {
      animatedSprite.scale.set(baseScale, baseScale);
    }

    // Position sprite at (0,0) relative to container
    animatedSprite.x = 0;
    animatedSprite.y = 0;

    // VIS-001-B: Add team ground indicator ellipse at unit's feet
    // Positioned below sprite center for 3D perspective effect
    const container = new PIXI.Container();
    const groundIndicator = new PIXI.Graphics();
    // Brighter, more saturated team colors for better visibility
    const teamColor = playerId === 'player1' ? 0x00AAFF : 0xFF3333;
    // Ground indicator matches sprite visual size (not footprint occupancy)
    const indicatorWidth = this.getHexGrid().hexSize * 0.85 * sizeMultiplier;
    const indicatorHeight = indicatorWidth * 0.4; // Flatter oval for ground perspective
    const feetOffset = (firstTexture?.height || 50) * baseScale * 0.4; // Position at feet

    groundIndicator.ellipse(0, feetOffset, indicatorWidth, indicatorHeight);
    groundIndicator.fill({ color: teamColor, alpha: 0.5 }); // 50% opacity for visibility
    container.addChild(groundIndicator);

    // VIS-003-A: Dark outline sprite removed per user feedback

    container.addChild(animatedSprite);

    container.x = position.x;
    container.y = position.y;

    console.log(`[CombatGridRenderer] DEBUG: Positioned container for ${unitId} at (${container.x.toFixed(1)}, ${container.y.toFixed(1)}) | hex: (${hex.q}, ${hex.r})`);

    // Setup animation completion handler for non-looping animations
    let onComplete: (() => void) | undefined;
    if (!animationData.loop) {
      onComplete = () => {
        this.triggerAnimationComplete({
          unitId,
          animationState
        });
      };
      animatedSprite.onComplete = onComplete;
    }

    // Add to stage
    this.getCreatureLayer().addChild(container);
    console.log(`[CombatGridRenderer] DEBUG: Added sprite to stage - unitId: ${unitId}, creatureLayer.children: ${this.getCreatureLayer().children.length}, container.visible: ${container.visible}, sprite.visible: ${animatedSprite.visible}`);

    // Start playing
    animatedSprite.play();
    console.log(`[CombatGridRenderer] DEBUG: Started playing animation for ${unitId}`);

    // Get battlefield views from renderCreatureWithId context
    // This will be passed from the calling method
    const battlefieldViews = (this as any).lastBattlefieldViews;
    const cachedDirection = (this as any).lastDirection;

    // Get cached projectile data
    const cachedProjectileData = (this as any).lastProjectileData;

    // Store sprite data (key by unitId)
    const spriteData: AnimatedSpriteData = {
      container,
      sprite: animatedSprite,
      unitId,
      animationState,
      isPlaying: true,
      loop: animationData.loop,
      textures: validTextures,
      scale: { x: animatedSprite.scale.x, y: animatedSprite.scale.y },
      x: container.x,
      y: container.y,
      battlefieldViews,
      playerId,
      direction: cachedDirection,
      projectile: cachedProjectileData,
      // L3-VIS-SUBHEX-001: Initialize sub-hex offset (calculated on first render frame)
      subHexOffset: { x: 0, y: 0 },
      // L3-MULTI-HEX: Store footprint for position updates
      footprint: cachedFootprint
    };

    // Only add onComplete if defined
    if (onComplete) {
      spriteData.onComplete = onComplete;
    }

    this.animatedSprites.set(unitId, spriteData);
  }

  /**
   * Render creature with animated sprite (async fallback path)
   */
  public async renderAnimatedCreature(
    hex: AxialCoordinate,
    unitId: string,
    playerId: 'player1' | 'player2',
    animationState: AnimationState,
    animationData: AnimationData,
    sizeMultiplier: number = 1.0,
    // PVE-FIX-003: Pass data directly to avoid race condition with concurrent async operations
    battlefieldViews?: any,
    direction?: number,
    projectileData?: CreatureProjectileData,
    footprint?: HexFootprint  // L3-MULTI-HEX: Footprint for multi-hex units
  ): Promise<void> {
    // Check if sprite already exists (may have been created by previous async operation)
    if (this.animatedSprites.has(unitId)) {
      console.log('[CombatGridRenderer] Sprite already exists for unit (skipping async load):', unitId);
      return;
    }

    // Check if already pending (prevent duplicate async operations)
    if (this.pendingCreations.has(unitId)) {
      console.log('[CombatGridRenderer] Async creation already pending for unit (skipping):', unitId);
      return;
    }

    // Add to pending tracker
    this.pendingCreations.add(unitId);

    try {
      // Validate inputs
      if (!animationData.frames || animationData.frames.length === 0) {
        throw new Error('Animation data must have at least one frame');
      }

      // SPAWN-TO-BATTLE: Allow off-grid coordinates for deployment positions
      // Units can spawn outside the combat grid and move into it
      const isOffGrid = !this.getHexGrid().isValid(hex);
      if (isOffGrid) {
        console.log('[CombatGridRenderer] Rendering at off-grid position (spawn-to-battle):', {
          unitId,
          hex,
          animationState
        });
      }

      // Check if this unit already has a sprite (should not happen, but defensive)
      const existingSprite = this.animatedSprites.get(unitId);
      if (existingSprite) {
        console.warn('[CombatGridRenderer] Sprite already exists for unit, destroying old one:', unitId);
        this.destroyAnimatedSprite(existingSprite);
        this.animatedSprites.delete(unitId);
      }

      // Load textures with individual error handling (prevent one bad URL from blocking all)
      console.log(`[CombatGridRenderer] Loading ${animationData.frames.length} textures for unit:`, unitId);
      console.log(`[CombatGridRenderer] Frame URLs:`, animationData.frames);

      const texturePromises = animationData.frames.map(async (frame, index) => {
        // Normalize frame URL to handle raw base64 strings (challenge creatures)
        const normalizedFrame = this.normalizeImageUrl(frame);

        // DIAGNOSTIC: Log actual frame data characteristics (not misleading shortUrl)
        const isDataUri = normalizedFrame.startsWith('data:');
        const rawBase64Start = frame.substring(0, 30);
        const pngBase64Header = 'iVBORw0KGgo'; // PNG magic bytes in base64
        const startsWithPngHeader = frame.startsWith(pngBase64Header) || frame.includes(pngBase64Header);
        console.log(`[CombatGridRenderer] [${unitId}] Frame ${index + 1}/${animationData.frames.length} DIAGNOSTIC:`, {
          rawLength: frame.length,
          normalizedLength: normalizedFrame.length,
          isDataUri,
          rawStart: rawBase64Start,
          startsWithPngHeader,
          isLikelyTruncated: frame.length < 1000,
          // Only show last 20 chars to check for valid base64 ending
          rawEnd: frame.substring(Math.max(0, frame.length - 20))
        });

        try {
          const startTime = performance.now();
          let texture: PIXI.Texture | null = null;

          // For data URIs, use Assets.load with proper registration (waits for actual image loading)
          if (normalizedFrame.startsWith('data:')) {
            // Generate unique asset key to avoid caching issues
            const assetKey = `combat-frame-${unitId}-${index}-${Date.now()}`;

            // Register the data URI asset
            if (!PIXI.Assets.cache.has(assetKey)) {
              PIXI.Assets.add({ alias: assetKey, src: normalizedFrame });
            }

            try {
              texture = await PIXI.Assets.load(assetKey);
            } catch (assetsError) {
              console.warn(`[CombatGridRenderer] [${unitId}] Assets.load failed for data URI, trying Texture.from fallback`);
              // Fallback: use Texture.from (PIXI.js v8)
              texture = PIXI.Texture.from(normalizedFrame);
              // Small delay for PIXI.js v8 to process the texture
              await new Promise(resolve => setTimeout(resolve, 10));
            }
          } else {
            // For HTTP URLs, use Assets.load directly
            texture = await PIXI.Assets.load(normalizedFrame);
          }

          // Validate texture was loaded successfully
          if (!texture || !texture.width || !texture.height) {
            throw new Error(`Texture loaded but has invalid dimensions: ${texture?.width}x${texture?.height}`);
          }

          const duration = Math.round(performance.now() - startTime);
          console.log(`[CombatGridRenderer] [${unitId}] ✓ Loaded frame ${index + 1}/${animationData.frames.length} in ${duration}ms (${texture.width}x${texture.height})`);
          return texture;
        } catch (err) {
          // Enhanced error logging with frame data context
          console.error(`[CombatGridRenderer] [${unitId}] ✗ FAILED frame ${index + 1}/${animationData.frames.length}:`, {
            error: err instanceof Error ? err.message : 'Unknown error',
            frameLength: frame.length,
            normalizedLength: normalizedFrame.length,
            startsWithPngHeader,
            isLikelyTruncated: frame.length < 1000
          });
          throw err; // Re-throw to fail the entire sprite creation (can't render partial sprite)
        }
      });

      const loadedTextures = await Promise.all(texturePromises);

      // Validate textures - filter out any null/undefined and ensure valid PIXI textures
      const textures = loadedTextures.filter((tex): tex is PIXI.Texture => {
        if (!tex) {
          console.error(`[CombatGridRenderer] [${unitId}] Null texture in loaded array`);
          return false;
        }
        // Check if it's a valid PIXI Texture with actual texture data
        if (!tex.source || tex.source.destroyed) {
          console.error(`[CombatGridRenderer] [${unitId}] Invalid/destroyed texture source`);
          return false;
        }
        return true;
      });

      if (textures.length === 0) {
        throw new Error(`All textures failed validation for unit ${unitId}`);
      }

      if (textures.length !== loadedTextures.length) {
        console.warn(`[CombatGridRenderer] [${unitId}] ${loadedTextures.length - textures.length} textures were invalid and filtered out`);
      }

      console.log(`[CombatGridRenderer] [${unitId}] ✓ All ${textures.length} textures loaded and validated successfully`);

      // L3-MULTI-HEX: Get footprint size for multi-hex units
      const footprintSize = footprint?.size ?? 1;

      // L3-MULTI-HEX: Calculate position at footprint visual center for multi-hex units
      let position: { x: number; y: number };
      if (footprint && footprintSize > 1) {
        const center = getFootprintCenter(hex, footprint);
        position = this.hexToPixel(center);
      } else {
        position = this.hexToPixel(hex);
      }

      // Create animated sprite
      const animatedSprite = new PIXI.AnimatedSprite(textures);

      // Configure animation
      animatedSprite.animationSpeed = animationData.frameDuration / 1000 * 1.0; // Convert ms to PixiJS speed (increased to 1.0 for proper sync with movement)
      animatedSprite.loop = animationData.loop;
      animatedSprite.anchor.set(0.5);

      // L3-MULTI-HEX-FIX: Footprint determines occupancy only, not visual scale
      const asyncFillRatio = footprintSize > 1 ? 1.0 : CREATURE_HEX_FILL_RATIO;
      const targetSize = this.getHexGrid().hexSize * asyncFillRatio * sizeMultiplier;
      const baseScale = Math.min(
        targetSize / textures[0].width,
        targetSize / textures[0].height
      );

      // Apply mirroring if needed
      if (animationData.shouldMirror) {
        animatedSprite.scale.set(-baseScale, baseScale);
      } else {
        animatedSprite.scale.set(baseScale, baseScale);
      }

      // Position sprite at (0,0) relative to container
      animatedSprite.x = 0;
      animatedSprite.y = 0;

      // VIS-001-B: Add team ground indicator ellipse at unit's feet
      // Positioned below sprite center for 3D perspective effect
      const container = new PIXI.Container();
      const groundIndicator = new PIXI.Graphics();
      // Brighter, more saturated team colors for better visibility
      const teamColor = playerId === 'player1' ? 0x00AAFF : 0xFF3333;
      // Ground indicator matches sprite visual size
      const indicatorWidth = this.getHexGrid().hexSize * 0.85 * sizeMultiplier;
      const indicatorHeight = indicatorWidth * 0.4; // Flatter oval for ground perspective
      const feetOffset = (textures[0]?.height || 50) * baseScale * 0.4; // Position at feet

      groundIndicator.ellipse(0, feetOffset, indicatorWidth, indicatorHeight);
      groundIndicator.fill({ color: teamColor, alpha: 0.5 }); // 50% opacity for visibility
      container.addChild(groundIndicator);

      // VIS-003-A: Dark outline sprite removed per user feedback

      container.addChild(animatedSprite);

      container.x = position.x;
      container.y = position.y;

      // Setup animation completion handler for non-looping animations
      let onComplete: (() => void) | undefined;
      if (!animationData.loop) {
        onComplete = () => {
          this.triggerAnimationComplete({
            unitId,
            animationState
          });
        };
        animatedSprite.onComplete = onComplete;
      }

      // Add to stage
      this.getCreatureLayer().addChild(container);

      // Start playing
      animatedSprite.play();

      // PVE-FIX-003: Use function parameters directly instead of global cache (fixes race condition)
      // Store sprite data (key by unitId, not hash)
      const spriteData: AnimatedSpriteData = {
        container,                        // Store container
        sprite: animatedSprite,
        unitId,
        animationState,
        isPlaying: true,
        loop: animationData.loop,
        textures,
        scale: { x: animatedSprite.scale.x, y: animatedSprite.scale.y },
        x: container.x,
        y: container.y,
        // PVE-FIX-003: Use parameters passed directly to avoid race condition with concurrent async operations
        battlefieldViews,
        playerId,
        direction,
        projectile: projectileData,
        // L3-VIS-SUBHEX-001: Initialize sub-hex offset (calculated on first render frame)
        subHexOffset: { x: 0, y: 0 },
        // L3-MULTI-HEX: Store footprint for position/scale updates
        footprint
      };

      // Only add onComplete if defined
      if (onComplete) {
        spriteData.onComplete = onComplete;
      }

      this.animatedSprites.set(unitId, spriteData);
    } finally {
      // Always remove from pending tracker (success or failure)
      this.pendingCreations.delete(unitId);
    }
  }

  /**
   * Render static sprite with unitId tracking (fallback for non-directional sprites)
   * This prevents ghost trails by using unitId instead of position hash
   */
  private async renderStaticSpriteWithId(
    unitId: string,
    hex: AxialCoordinate,
    playerId: 'player1' | 'player2',
    spriteUrl: string,
    opacity: number = 1.0,
    sizeMultiplier: number = 1.0
  ): Promise<void> {
    // Check if unit already has a sprite
    const existing = this.animatedSprites.get(unitId);

    if (existing) {
      // UPDATE EXISTING SPRITE IN-PLACE
      this.updateSpritePosition(existing, hex);
      if (existing.container.alpha !== opacity) {
        existing.container.alpha = opacity;
      }
      return;
    }

    // NEW UNIT - Create static sprite
    try {
      // Load texture with proper handling for data URIs
      let texture: PIXI.Texture | null = null;

      if (spriteUrl.startsWith('data:')) {
        // For data URIs, register and load via Assets to ensure proper loading
        const assetKey = `static-sprite-${unitId}-${Date.now()}`;
        if (!PIXI.Assets.cache.has(assetKey)) {
          PIXI.Assets.add({ alias: assetKey, src: spriteUrl });
        }
        try {
          texture = await PIXI.Assets.load(assetKey);
        } catch (assetsError) {
          console.warn(`[CombatGridRenderer] [${unitId}] Assets.load failed for static data URI, trying Texture.from fallback`);
          texture = PIXI.Texture.from(spriteUrl);
          // Small delay for PIXI.js v8 to process the texture
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      } else {
        // For HTTP URLs, use Assets.load directly
        texture = await PIXI.Assets.load(spriteUrl);
      }

      // Validate texture
      if (!texture || !texture.width || !texture.height) {
        throw new Error(`Static sprite texture invalid: ${texture?.width}x${texture?.height}`);
      }

      console.log(`[CombatGridRenderer] [${unitId}] Static sprite loaded: ${texture.width}x${texture.height}`);

      // Calculate position using consistent offset (uses config.canvasWidth, not canvas buffer width)
      const position = this.hexToPixel(hex);

      // Create static sprite
      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(0.5);

      // Scale sprite to fill hex — CREATURE_HEX_FILL_RATIO allows visual overflow
      const targetSize = this.getHexGrid().hexSize * CREATURE_HEX_FILL_RATIO * sizeMultiplier;
      const scale = Math.min(
        targetSize / texture.width,
        targetSize / texture.height
      );
      sprite.scale.set(scale, scale);

      // Position sprite at (0,0) relative to container
      sprite.x = 0;
      sprite.y = 0;

      // VIS-001-B: Add team ground indicator ellipse at unit's feet
      const container = new PIXI.Container();
      const groundIndicator = new PIXI.Graphics();
      // Brighter, more saturated team colors for better visibility
      const teamColor = playerId === 'player1' ? 0x00AAFF : 0xFF3333;
      // Ground indicator scaled proportionally with sprite
      const indicatorWidth = this.getHexGrid().hexSize * 0.85 * sizeMultiplier;
      const indicatorHeight = indicatorWidth * 0.4; // Flatter oval for ground perspective
      const feetOffset = (texture?.height || 50) * scale * 0.4; // Position at feet

      groundIndicator.ellipse(0, feetOffset, indicatorWidth, indicatorHeight);
      groundIndicator.fill({ color: teamColor, alpha: 0.5 }); // 50% opacity for visibility
      container.addChild(groundIndicator);

      // VIS-003-A: Dark outline sprite removed per user feedback

      container.addChild(sprite);

      container.x = position.x;
      container.y = position.y;
      container.alpha = opacity;

      // Add to stage
      this.getCreatureLayer().addChild(container);

      // Store sprite data (using AnimatedSpriteData structure for consistency)
      const spriteData: AnimatedSpriteData = {
        container,
        sprite: sprite as any, // Static sprite treated as single-frame animation
        unitId,
        animationState: AnimationState.IDLE,
        isPlaying: false, // Static sprite doesn't animate
        loop: false,
        textures: [texture],
        scale: { x: sprite.scale.x, y: sprite.scale.y },
        x: container.x,
        y: container.y,
        // L3-VIS-SUBHEX-001: Initialize sub-hex offset (calculated on first render frame)
        subHexOffset: { x: 0, y: 0 }
      };

      this.animatedSprites.set(unitId, spriteData);
    } catch (error) {
      console.error('[CombatGridRenderer] Failed to load static sprite:', error);
    }
  }

  /**
   * Get sprite at hex position
   * @deprecated Use getSpriteByUnitId instead - sprites are keyed by unitId, not hex hash
   */
  public getSpriteAt(hex: AxialCoordinate): AnimatedSpriteData | undefined {
    const hash = this.getHexGrid().hash(hex);
    return this.animatedSprites.get(hash);
  }

  /**
   * Get sprite by unit ID (correct method)
   * Sprites are stored by unitId, so this method provides proper lookup
   */
  public getSpriteByUnitId(unitId: string): AnimatedSpriteData | undefined {
    return this.animatedSprites.get(unitId);
  }

  /**
   * Get projectile data for a unit's creature
   * Returns the creature's projectile sprite frames if available
   */
  public getProjectileDataByUnitId(unitId: string): CreatureProjectileData | undefined {
    const spriteData = this.animatedSprites.get(unitId);
    return spriteData?.projectile;
  }

  /**
   * Get all active animated sprites
   */
  public getActiveSprites(): Map<string, AnimatedSpriteData> {
    return this.animatedSprites;
  }

  /**
   * Convert hex coordinates to screen pixel coordinates
   * Includes proper grid centering and offset calculations
   *
   * IMPORTANT: Uses parent's getGridOffset() which uses config.canvasWidth/Height
   * to ensure consistent coordinate transforms with camera system.
   * Do NOT use this.getCanvas().width here - it returns actual buffer dimensions
   * which differ from CSS dimensions on high-DPI displays (devicePixelRatio > 1).
   *
   * @param hex - Hex coordinate to convert
   * @returns Pixel coordinates {x, y} relative to canvas
   */
  public override hexToPixel(hex: AxialCoordinate): { x: number; y: number } {
    // Use parent's cached offset for consistency with camera system
    const offset = this.getGridOffset();
    const pixel = this.getHexGrid().toPixel(hex);

    return {
      x: pixel.x + offset.x,
      y: pixel.y + offset.y
    };
  }

  /**
   * Update all animations (call each frame at 60 FPS)
   */
  public updateAnimations(deltaTime: number): void {
    this.lastUpdateTime += deltaTime;

    // Update terrain renderer (inherited from parent)
    if (this.terrainRenderer) {
      this.terrainRenderer.update(deltaTime);
    }

    // PixiJS AnimatedSprite updates automatically via ticker
    // This method is here for manual control if needed
    this.animatedSprites.forEach(spriteData => {
      if (spriteData.isPlaying && !spriteData.sprite.playing) {
        spriteData.sprite.play();
      }
    });
  }

  /**
   * Register callback for animation completion
   */
  public onAnimationComplete(callback: (event: AnimationCompleteEvent) => void): void {
    this.animationCompleteCallbacks.push(callback);
  }

  /**
   * VIS-002-A/B: Flash sprite on damage for visual feedback
   * White flash for normal damage, orange for critical hits
   *
   * @param unitId - Unit to flash
   * @param color - Flash color (default white 0xFFFFFF, orange 0xFF6600 for crit)
   * @param duration - Flash duration in ms (default 80ms, 120ms for crit)
   */
  public flashSprite(unitId: string, color: number = 0xFFFFFF, duration: number = 80): void {
    const spriteData = this.animatedSprites.get(unitId);
    if (!spriteData?.sprite) return;

    // Store original tint (default is white/no tint = 0xFFFFFF)
    const originalTint = spriteData.sprite.tint;

    // Apply flash tint
    spriteData.sprite.tint = color;

    // Reset to original after duration
    setTimeout(() => {
      if (spriteData.sprite && !spriteData.sprite.destroyed) {
        spriteData.sprite.tint = originalTint;
      }
    }, duration);
  }

  /**
   * Apply a wind-up offset to a unit's sprite (temporary lean toward/away from target).
   * Used to telegraph attack start before damage lands.
   */
  public applyWindupOffset(unitId: string, offsetX: number, offsetY: number, durationMs: number): void {
    const spriteData = this.animatedSprites.get(unitId);
    if (!spriteData) return;

    spriteData.windupOffset = {
      x: offsetX,
      y: offsetY,
      expiresAt: performance.now() + durationMs
    };
  }

  /**
   * Remove creature by unit ID (combat-specific method)
   */
  public removeCreatureById(unitId: string): void {
    const spriteData = this.animatedSprites.get(unitId);

    if (spriteData) {
      this.destroyAnimatedSprite(spriteData);
      this.animatedSprites.delete(unitId);
    }
  }

  /**
   * Remove creature by hex position (legacy method for backward compatibility)
   */
  public override removeCreature(hex: AxialCoordinate): void {
    // Scan for unit at this position in animated sprites
    for (const [unitId, spriteData] of Array.from(this.animatedSprites.entries())) {
      // Check if container is at this position
      const containerPos = this.getHexFromPixel({
        x: spriteData.container.x,
        y: spriteData.container.y
      });

      if (containerPos && this.hexEquals(containerPos, hex)) {
        this.removeCreatureById(unitId);
        return;
      }
    }

    // Fallback to parent
    super.removeCreature(hex);
  }

  /**
   * Clear all creatures including animated sprites
   */
  public override clearAllCreatures(): void {
    // Destroy all animated sprites
    this.animatedSprites.forEach(spriteData => {
      this.destroyAnimatedSprite(spriteData);
    });
    this.animatedSprites.clear();

    // Call parent method
    super.clearAllCreatures();
  }

  /**
   * Destroy the renderer and cleanup resources
   */
  public override destroy(): void {
    // Stop all animations and clear sprites
    this.clearAllCreatures();

    // Call parent destroy (which handles terrain cleanup)
    super.destroy();
  }

  /**
   * Destroy an animated sprite and its resources
   */
  private destroyAnimatedSprite(spriteData: AnimatedSpriteData): void {
    // Stop animation
    spriteData.sprite.stop();

    // Remove CONTAINER from stage (not just sprite from container!)
    if (spriteData.container.parent) {
      spriteData.container.parent.removeChild(spriteData.container);
    }

    // Destroy container (which destroys children including sprite)
    spriteData.container.destroy({
      children: true,
      texture: false,  // Don't destroy shared textures
      textureSource: false
    });
  }

  /**
   * Trigger animation completion callbacks
   */
  private triggerAnimationComplete(event: AnimationCompleteEvent): void {
    this.animationCompleteCallbacks.forEach(callback => callback(event));
  }

  /**
   * Expose protected method for positioning calculations
   */
  private calculateGridBoundsPublic() {
    // Access protected method via type assertion
    const hexGrid = this.getHexGrid();
    const { width, height } = hexGrid.getConfig();

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    // Sample all edge hexes to find true bounds after projection
    for (let q = 0; q < width; q++) {
      for (let r = 0; r < height; r++) {
        // Only check edge hexes for efficiency
        if (q === 0 || q === width - 1 || r === 0 || r === height - 1) {
          const pixel = hexGrid.toPixel({ q, r });
          minX = Math.min(minX, pixel.x);
          maxX = Math.max(maxX, pixel.x);
          minY = Math.min(minY, pixel.y);
          maxY = Math.max(maxY, pixel.y);
        }
      }
    }

    // Add hex size padding
    const padding = hexGrid.hexSize;

    return {
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2,
      minX: minX - padding,
      minY: minY - padding
    };
  }

  /**
   * Get creature layer for adding sprites
   */
  private getCreatureLayer(): PIXI.Container {
    // Access private property via type assertion
    return (this as any).creatureLayer;
  }

  /**
   * Get canvas element
   */
  public override getCanvas(): HTMLCanvasElement {
    return super.getCanvas();
  }

  /**
   * Update sprite position without recreating
   * L3-MULTI-HEX: Uses footprint center for multi-hex units
   */
  private updateSpritePosition(
    spriteData: AnimatedSpriteData,
    hex: AxialCoordinate
  ): void {
    // L3-MULTI-HEX: Calculate position at footprint visual center for multi-hex units
    let position: { x: number; y: number };
    if (spriteData.footprint && spriteData.footprint.size > 1) {
      const center = getFootprintCenter(hex, spriteData.footprint);
      position = this.hexToPixel(center);
    } else {
      position = this.hexToPixel(hex);
    }

    // Apply wind-up offset if active (attack lean toward/away from target)
    let windupX = 0;
    let windupY = 0;
    if (spriteData.windupOffset) {
      if (performance.now() < spriteData.windupOffset.expiresAt) {
        windupX = spriteData.windupOffset.x;
        windupY = spriteData.windupOffset.y;
      } else {
        spriteData.windupOffset = undefined; // Expired
      }
    }

    spriteData.container.x = position.x + windupX;
    spriteData.container.y = position.y + windupY;
  }

  /**
   * Extract battlefield views from sprite data
   */
  private extractBattlefieldViews(spriteData: any): any {
    if (!spriteData) return null;

    if (spriteData.battlefieldDirectionalViews) {
      // DIAGNOSTIC: Log attack frame availability for each direction
      console.log('[CombatGridRenderer] CREATURE SPRITE DATA DIAGNOSTIC:', {
        hasE: !!spriteData.battlefieldDirectionalViews.E,
        eAttackFrames: spriteData.battlefieldDirectionalViews.E?.attackFrames?.length ?? 0,
        eIdleFrames: spriteData.battlefieldDirectionalViews.E?.idleFrames?.length ?? 0,
        eWalkFrames: spriteData.battlefieldDirectionalViews.E?.walkFrames?.length ?? 0,
        hasNE: !!spriteData.battlefieldDirectionalViews.NE,
        neAttackFrames: spriteData.battlefieldDirectionalViews.NE?.attackFrames?.length ?? 0,
        hasSE: !!spriteData.battlefieldDirectionalViews.SE,
        seAttackFrames: spriteData.battlefieldDirectionalViews.SE?.attackFrames?.length ?? 0
      });
      return spriteData.battlefieldDirectionalViews;
    }

    return null;
  }

  /**
   * Extract projectile data from sprite data
   * Returns projectile sprite frames if the creature has ranged attacks
   */
  private extractProjectileData(spriteData: any): CreatureProjectileData | undefined {
    if (!spriteData?.projectile) return undefined;

    const projectile = spriteData.projectile;

    // Validate projectile data structure
    if (!projectile.frames || !Array.isArray(projectile.frames) || projectile.frames.length === 0) {
      console.log('[CombatGridRenderer] Invalid projectile data - missing or empty frames');
      return undefined;
    }

    console.log('[CombatGridRenderer] Extracted projectile data:', {
      key: projectile.key,
      element: projectile.element,
      frameCount: projectile.frames.length
    });

    return {
      key: projectile.key || 'unknown_projectile',
      frames: projectile.frames,
      element: projectile.element || 'magic'
    };
  }

  /**
   * L3-MULTI-HEX: Extract footprint data from sprite data
   * Returns footprint for multi-hex unit positioning and scaling
   */
  private extractFootprint(spriteData: any): HexFootprint | undefined {
    if (!spriteData?.footprint) return undefined;

    const footprint = spriteData.footprint;

    // Validate footprint structure
    if (typeof footprint.size !== 'number' || !Array.isArray(footprint.relativeHexes)) {
      console.log('[CombatGridRenderer] Invalid footprint data structure');
      return undefined;
    }

    // Only log for multi-hex units (size > 1)
    if (footprint.size > 1) {
      console.log('[CombatGridRenderer] Extracted multi-hex footprint:', {
        size: footprint.size,
        relativeHexes: footprint.relativeHexes
      });
    }

    return footprint as HexFootprint;
  }

  /**
   * Select animation for direction and state
   */
  private selectDirectionalAnimation(
    views: any,
    direction: number,
    animationState: AnimationState = AnimationState.IDLE
  ): AnimationData {
    // Map direction to view key (HexDirection: NE=0, E=1, SE=2, SW=3, W=4, NW=5)
    // Western directions (SW=3, W=4, NW=5) mirror their eastern counterpart
    const isWestFacing = direction >= 3 && direction <= 5;
    const mirrorMap: Record<number, string> = { 3: 'SE', 4: 'E', 5: 'NE' };
    const dirKey = isWestFacing
      ? mirrorMap[direction] || 'E'
      : direction === 0 ? 'NE' : direction === 1 ? 'E' : 'SE';
    const view = views[dirKey] || views['E']; // Fallback to E

    // Select frames based on animation state
    let frames: string[];
    let loop: boolean;

    switch (animationState) {
      case AnimationState.DEATH:
        // Death animation - one-shot, terminal state
        console.log('[CombatGridRenderer] DEATH FRAME DIAGNOSTIC:', {
          dirKey,
          hasDeathFrames: !!view.deathFrames,
          deathFramesCount: view.deathFrames?.length ?? 0,
          hasSprite: !!view.sprite
        });
        // FIX: Empty arrays are truthy, so explicitly check length
        // This fixes creatures without death animation falling back to sprite correctly
        frames = view.deathFrames && view.deathFrames.length > 0 ? view.deathFrames : [view.sprite];
        loop = false; // Death is one-shot (terminal)
        break;

      case AnimationState.CAST:
        // Cast animation - one-shot (falls back to attack if no cast frames)
        // FIX: Empty arrays are truthy, so explicitly check length for each fallback
        frames = (view.castFrames && view.castFrames.length > 0) ? view.castFrames
               : (view.attackFrames && view.attackFrames.length > 0) ? view.attackFrames
               : (view.attack && view.attack.length > 0) ? view.attack
               : [view.sprite];
        loop = false; // Cast is one-shot
        break;

      case AnimationState.ATTACK:
        // DIAGNOSTIC: Log attack frame data availability
        console.log('[CombatGridRenderer] ATTACK FRAME DIAGNOSTIC:', {
          dirKey,
          hasAttackFrames: !!view.attackFrames,
          attackFramesCount: view.attackFrames?.length ?? 0,
          hasAttack: !!view.attack,
          attackCount: view.attack?.length ?? 0,
          hasSprite: !!view.sprite
        });
        // FIX: Empty arrays are truthy, so explicitly check length for each fallback
        const baseAttackFrames = (view.attackFrames && view.attackFrames.length > 0) ? view.attackFrames
               : (view.attack && view.attack.length > 0) ? view.attack
               : [view.sprite];

        // VIS-005-A: Attack anticipation - duplicate first frame for "wind-up" hold effect
        // This makes the attack feel more deliberate and readable
        if (baseAttackFrames.length > 1) {
          // Add first frame twice more at the start for anticipation hold
          frames = [baseAttackFrames[0], baseAttackFrames[0], ...baseAttackFrames];
        } else {
          frames = baseAttackFrames;
        }
        loop = false; // Attack is one-shot
        break;

      case AnimationState.WALK:
        // FIX: Empty arrays are truthy, so explicitly check length
        frames = (view.walkFrames && view.walkFrames.length > 0) ? view.walkFrames
               : (view.walk && view.walk.length > 0) ? view.walk
               : [view.sprite];
        loop = true;
        break;

      case AnimationState.IDLE:
      default:
        // FIX: Empty arrays are truthy, so explicitly check length
        frames = (view.idleFrames && view.idleFrames.length > 0) ? view.idleFrames
               : (view.idle && view.idle.length > 0) ? view.idle
               : [view.sprite];
        loop = true;
        break;
    }

    // State-aware frame duration (ms per frame)
    const frameDuration = animationState === AnimationState.ATTACK ? 180
                        : animationState === AnimationState.WALK ? 120
                        : animationState === AnimationState.DEATH ? 200
                        : 100; // IDLE default

    return {
      frames,
      frameDuration,
      loop,
      shouldMirror: isWestFacing
    };
  }

  /**
   * Check if two hexes are equal
   */
  private hexEquals(a: AxialCoordinate, b: AxialCoordinate): boolean {
    return a.q === b.q && a.r === b.r;
  }

  /**
   * Get hex coordinate from pixel position (inverse of toPixel)
   */
  private getHexFromPixel(pixel: { x: number; y: number }): AxialCoordinate | null {
    // Remove offsets using consistent offset via getGridOffset (uses config.canvasWidth)
    const offset = this.getGridOffset();

    const adjustedX = pixel.x - offset.x;
    const adjustedY = pixel.y - offset.y;

    // Use hex grid's fromPixel method
    return this.getHexGrid().fromPixel({ x: adjustedX, y: adjustedY });
  }
}
